import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { ASSETS, SERVER } from "@/data/content";
import { CopyIp } from "./CopyIp";
import { ServerStatus } from "./ServerStatus";
import { DiscordButton } from "./DiscordButton";
import { PeakSparkline } from "./PeakSparkline";
import { EASE } from "./Reveal";

const LINES = ["Bone County", "Roleplay"];

const Line = ({ text, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease: EASE }}
    >
      {text}
    </motion.span>
  </span>
);

const Logo3D = ({ rx, ry }) => (
  <motion.div
    style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1.4, delay: 0.6, ease: EASE }}
    className="relative hidden lg:block"
    data-testid="hero-logo"
  >
    <div className="absolute inset-0 -z-10 rounded-full bg-[radial-gradient(circle,rgba(94,177,255,0.35),transparent_65%)] blur-3xl" />
    <motion.img
      src={ASSETS.logo}
      alt="Bone County Roleplay logó"
      className="h-[22rem] w-[22rem] object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
      animate={{ y: [0, -14, 0] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      style={{ translateZ: 40 }}
    />
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 90, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 90, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative flex min-h-[100svh] items-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
        <motion.img
          src={ASSETS.heroBg}
          alt=""
          initial={{ opacity: 0, filter: "blur(18px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 2, ease: EASE }}
          className="h-full w-full object-cover object-center"
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#0a0a0b]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/25 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b]/70 via-[#0a0a0b]/10 to-transparent" />

      <motion.div
        aria-hidden
        className="pointer-events-none absolute -left-40 top-1/3 h-[42rem] w-[42rem] rounded-full bg-[radial-gradient(circle,rgba(94,177,255,0.22),transparent_60%)] blur-3xl"
        animate={{ x: [0, 60, 0], y: [0, -40, 0], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute right-0 top-0 h-[36rem] w-[36rem] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.08),transparent_60%)] blur-3xl"
        animate={{ x: [0, -40, 0], y: [0, 50, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 items-end gap-10 px-5 pb-16 pt-40 sm:px-8 lg:grid-cols-[1fr_auto] lg:pb-24"
      >
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
            className="eyebrow mb-8"
            data-testid="hero-eyebrow"
          >
            MTA Roleplay szerver — bc-rp.hu
          </motion.p>

          <h1
            className="font-display text-[clamp(2.75rem,9vw,7.5rem)] font-bold uppercase leading-[0.92] tracking-[-0.03em] text-white"
            data-testid="hero-title"
          >
            {LINES.map((l, i) => <Line key={l} text={l} i={i} />)}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4"
          >
            <a
              href={SERVER.mtaLink}
              data-testid="hero-join-button"
              className="btn-glow inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-black"
            >
              Csatlakozz most
              <ArrowUpRight size={16} />
            </a>
            <DiscordButton href={SERVER.discord} testId="hero-discord-button" />
            <CopyIp testId="hero-copy-ip" />
            <ServerStatus />
          </motion.div>
          <PeakSparkline />
        </div>

        <Logo3D rx={rx} ry={ry} />
      </motion.div>

      <motion.a
        href="#rolunk"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/40 transition-colors hover:text-white sm:flex"
        data-testid="hero-scroll-hint"
        aria-label="Görgess lejjebb"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.a>
    </section>
  );
};
